import { useState, useEffect } from 'react';
import { useClipboard } from '../../hooks/useClipboard';
import { useRaydium } from '../../hooks/useRaydium';
import {
    AddressLookupTableAccount,
    AddressLookupTableProgram,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { getAssociatedTokenAddress, getOrCreateAssociatedTokenAccount } from '@solana/spl-token';
import { useSplToken } from '../../hooks/useSplToken';
import {
    MAINNET_PROGRAM_ID,
    TxVersion,
    Market,
    LOOKUP_TABLE_CACHE,
    ComputeBudgetConfig,
    buildSimpleTransaction,
    WSOL,
    Liquidity,
    InnerSimpleV0Transaction,
    LiquidityPoolKeysV4,
} from '@raydium-io/raydium-sdk';
import { formatAmmKeysByAccountData } from '../../utils/format-amm-keys-by-accountData';
import { useJito } from '../../hooks/useJito';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { deployPoolJitoTipDefault } from '../../../ConfigFees';

import deployAmmPoolData from '../../interfaces/deploy-amm-pool-data';
import BN from 'bn.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import bs58 from 'bs58';
import '../../DeployPool.css';

const swapsPerTransaction = 8;
const transactionsNumber = 4;

export function DeployPool({
    connection,
    wallet,
    buyers,
    bundleBuyValues,
    deployedMarketId,
    logMessage,
    injectPoolKeys,
}: {
    connection: Connection;
    wallet: Keypair;
    buyers: Keypair[];
    bundleBuyValues: number[];
    deployedMarketId: string | undefined;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    injectPoolKeys: (poolKeys: LiquidityPoolKeysV4) => void;
}) {
    const { getSwapInstructions } = useRaydium();
    const { getdeployAmmPoolInstructions, buildSimpleTransactionWithInstructionsArray } = useRaydium();
    const { getWalletTokenAccounts, getTokenAccountBalance, createTokenAccountsWithJito, transferAndWrapBatchWithJito } = useSplToken();
    const { getTipInstructions, sendBundle, spamAndConfirmBundle } = useJito();
    const { isCopied: isMarketIdCopied, copyToClipboard: copyMarketId } = useClipboard();
    const { isCopied: isBaseTokenCopied, copyToClipboard: copyBaseToken } = useClipboard();
    const { isCopied: isQuoteTokenCopied, copyToClipboard: copyQuoteToken } = useClipboard();

    const [openBookMarketId, setOpenBookMarketId] = useState<string>(deployedMarketId || '');

    const [baseTokenMint, setBaseTokenMint] = useState<string>('');
    const [quoteTokenMint, setQuoteTokenMint] = useState<string>('');

    const [baseTokenBalance, setBaseTokenBalance] = useState<number>(0);
    const [baseTokenDecimals, setBaseTokenDecimals] = useState<number>(0);
    const [baseTokenInitialLiquidity, setBaseTokenInitialLiquidity] = useState<number>(0);
    const [quoteTokenBalance, setQuoteTokenBalance] = useState<number>(0);
    const [quoteTokenDecimals, setQuoteTokenDecimals] = useState<number>(0);
    const [quoteTokenInitialLiquidity, setQuoteTokenInitialLiquidity] = useState<number>(0);

    const [advancedSettingsStatus, setAdvancedSettingsStatus] = useState<boolean>(true);
    const [advancedSettingsAddPurchaseStatus, setAdvancedSettingsAddPurchaseStatus] = useState<boolean>(true);

    const tipPayerOptions = [0, 1, 2]; //'Deployer', 'First Buyer' 'Last Buyer'
    const tipPayerLabels = ['Deployer', 'First Buyer', 'Last Buyer'];
    const [tipPayer, setTipPayer] = useState(tipPayerOptions[2]);

    const [bundleMode, setBundleMode] = useState<string>('Classic');
    const bundleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBundleMode(event.target.value);
    };

    const feePayerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTipPayer(parseInt(event.target.value, 10));
    };

    const [advancedSettingsIsJitoTx, setAdvancedSettingsIsJitoTx] = useState<boolean>(true);
    const [advancedSettingsJitoTip, setAdvancedSettingsJitoTip] = useState<number>(deployPoolJitoTipDefault);
    const [advancedSettingsSkipPreflight, setAdvancedSettingsSkipPreflight] = useState<boolean>(false);

    const [startTimeDelaySecondsStatus, setStartTimeDelaySecondsStatus] = useState<boolean>(false);
    const [startTimeDelayDate, setStartTimeDelayDate] = useState<Date | null>(null);
    const [startTimeEpoch, setStartTimeEpoch] = useState<number | null>(0);

    dayjs.extend(utc);
    dayjs.extend(timezone);

    const openBookMarketIdChange = (event: { target: { value: string } }) => {
        setOpenBookMarketId(event.target.value);
    };

    const baseTokenInitialLiquidityChange = (event: { target: { value: string } }) => {
        setBaseTokenInitialLiquidity(Number(event.target.value));
    };

    const maxButtonForBaseToken = () => {
        setBaseTokenInitialLiquidity(baseTokenBalance);
    };

    const quoteTokenInitialLiquidityChange = (event: { target: { value: string } }) => {
        setQuoteTokenInitialLiquidity(Number(event.target.value));
    };

    const maxButtonForQuoteToken = () => {
        setQuoteTokenInitialLiquidity(quoteTokenBalance);
    };

    const advancedSettingsStatusChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsStatus(checked);
    };

    const advancedSettingsAddPurchaseStatusChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsAddPurchaseStatus(checked);
    };

    const advancedSettingsIsJitoTxChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsIsJitoTx(checked);
    };

    const advancedSettingsJitoTipChange = (event: { target: { value: string } }) => {
        setAdvancedSettingsJitoTip(Number(event.target.value) * LAMPORTS_PER_SOL);
    };

    const startTimeDelaySecondsStatusChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setStartTimeDelaySecondsStatus(checked);
    };

    const convertDateToEpoch = (dateString: Date | null): number | null => {
        if (dateString) {
            const epochTime = Date.parse(dateString.toString());
            return epochTime;
        } else {
            return null;
        }
    };

    const startTimeDelayDateChange = (newDate: Date | null) => {
        if (newDate) {
            setStartTimeDelayDate(newDate);
            setStartTimeEpoch(convertDateToEpoch(newDate));
        }
    };

    const advancedSettingsSkipPreflightChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsSkipPreflight(checked);
    };

    useEffect(() => {
        if (openBookMarketId) {
            fetchOpenbookMarketInfo(connection, wallet, openBookMarketId);
        }
    }, [openBookMarketId]);

    async function fetchOpenbookMarketInfo(connection: Connection, wallet: Keypair, openbookMarketId: string) {
        try {
            const market = await connection.getAccountInfo(new PublicKey(openbookMarketId));
            const buffedMarket = Buffer.from(market?.data || new Uint8Array(0));

            const marketLayout = Market.getStateLayout(3);
            const marketData = marketLayout.decode(buffedMarket);

            const mintA = new PublicKey(marketData.baseMint);
            const mintB = new PublicKey(marketData.quoteMint);
            setOpenBookMarketId(openbookMarketId);
            setBaseTokenMint(mintA.toBase58());
            setQuoteTokenMint(mintB.toBase58());

            if (mintA.toBase58() === WSOL.mint) {
                const walletSolBalance = await connection.getBalance(wallet.publicKey, 'processed');
                setBaseTokenBalance(walletSolBalance / 1e9);
                setBaseTokenDecimals(9);
                setBaseTokenInitialLiquidity(79);
            } else {
                const walletTokenAccountA = await getOrCreateAssociatedTokenAccount(connection, wallet, mintA, wallet.publicKey);
                const tokenAccountBalanceA = await getTokenAccountBalance(connection, new PublicKey(walletTokenAccountA.address));
                setBaseTokenBalance(tokenAccountBalanceA.uiAmount);
                setBaseTokenDecimals(tokenAccountBalanceA.decimals);
                setBaseTokenInitialLiquidity(tokenAccountBalanceA.uiAmount);
            }

            if (mintB.toBase58() === WSOL.mint) {
                const walletSolBalance = await connection.getBalance(wallet.publicKey);
                setQuoteTokenBalance(walletSolBalance / 1e9);
                setQuoteTokenDecimals(9);
                setQuoteTokenInitialLiquidity(79);
            } else {
                const walletTokenAccountB = await getOrCreateAssociatedTokenAccount(connection, wallet, mintB, wallet.publicKey);
                const tokenAccountBalanceB = await getTokenAccountBalance(connection, new PublicKey(walletTokenAccountB.address));
                setQuoteTokenBalance(tokenAccountBalanceB.uiAmount);
                setQuoteTokenDecimals(tokenAccountBalanceB.decimals);
                setQuoteTokenInitialLiquidity(tokenAccountBalanceB.uiAmount);
            }
        } catch (error) {
            logMessage(`Fetching OpenBook Market Info:  ${error}`);
        }
    }

    async function deployAmmPool(connection: Connection, wallet: Keypair) {
        if (!openBookMarketId) return logMessage('OpenBook Market ID is required');

        logMessage('Deploying AMM Pool...');

        const walletTokenAccounts = await getWalletTokenAccounts(connection, wallet.publicKey);
        // const priorityFeeLamborts = Math.floor((await getRecentPrioritizationFees()).high);
        // logMessage(`Priority Fee: ${priorityFeeLamborts}`);

        const computeBudgetConfig: ComputeBudgetConfig = {
            microLamports: 1000,
        };
        const deployAmmPoolData: deployAmmPoolData = {
            connection,
            programId: MAINNET_PROGRAM_ID.AmmV4,
            marketInfo: {
                marketId: new PublicKey(openBookMarketId),
                programId: MAINNET_PROGRAM_ID.OPENBOOK_MARKET,
            },
            baseMintInfo: {
                mint: new PublicKey(baseTokenMint),
                decimals: baseTokenDecimals,
            },
            quoteMintInfo: {
                mint: new PublicKey(quoteTokenMint),
                decimals: quoteTokenDecimals,
            },
            baseAmount: new BN(baseTokenInitialLiquidity * Math.pow(10, baseTokenDecimals)),
            quoteAmount: new BN(quoteTokenInitialLiquidity * Math.pow(10, quoteTokenDecimals)),
            startTime: advancedSettingsStatus && startTimeDelaySecondsStatus && startTimeEpoch !== null ? new BN(startTimeEpoch) : new BN(0),
            ownerInfo: {
                feePayer: wallet.publicKey,
                wallet: wallet.publicKey,
                tokenAccounts: walletTokenAccounts,
                useSOLBalance: true,
            },
            associatedOnly: false,
            computeBudgetConfig,
            checkCreateATAOwner: true,
            makeTxVersion: TxVersion.V0,
            feeDestinationId: new PublicKey('7YttLkHDoNj9wyDur5pM1ejNaAvT9X4eqaYcHQqtj2G5'), //7YttLkHDoNj9wyDur5pM1ejNaAvT9X4eqaYcHQqtj2G5
        };
        const deployPoolInstructions = await getdeployAmmPoolInstructions(deployAmmPoolData);

        const deploymentVersionedTransaction = (
            await buildSimpleTransaction({
                connection,
                makeTxVersion: TxVersion.V0,
                payer: wallet.publicKey,
                innerTransactions: deployPoolInstructions.innerTransactions,
                addLookupTableInfo: LOOKUP_TABLE_CACHE,
            })
        )[0] as VersionedTransaction;
        deploymentVersionedTransaction.sign([wallet]);

        let sigToConfirm = bs58.encode(deploymentVersionedTransaction.signatures[0]);

        const jitoVersionedTransactions: VersionedTransaction[] = [];
        let poolKeys: LiquidityPoolKeysV4;
        if (advancedSettingsStatus && advancedSettingsIsJitoTx && advancedSettingsAddPurchaseStatus) {
            //temporary thing, modes will be removed soon
            //code is just a copy + paste
            if (bundleMode === 'Classic') {
                logMessage(`Creating token accounts with Jito..`);
                const createInAccountsPromise = createTokenAccountsWithJito(
                    connection,
                    buyers,
                    new PublicKey(baseTokenMint),
                    'processed',
                    advancedSettingsJitoTip,
                    logMessage,
                );
                const createOutAccountsPromise = createTokenAccountsWithJito(
                    connection,
                    buyers,
                    new PublicKey(quoteTokenMint),
                    'processed',
                    advancedSettingsJitoTip,
                    logMessage,
                );
                await Promise.all([createInAccountsPromise, createOutAccountsPromise]);
                logMessage(`Token accounts created..`);

                jitoVersionedTransactions.push(deploymentVersionedTransaction);
                //making swap transaction
                const ammId = deployPoolInstructions.address.ammId;
                const lpMint = deployPoolInstructions.address.lpMint;

                const simulated = await connection.simulateTransaction(deploymentVersionedTransaction, {
                    accounts: {
                        encoding: 'base64',
                        addresses: [ammId.toBase58(), lpMint.toBase58()],
                    },
                });

                if (
                    simulated.value.accounts === null ||
                    simulated.value.accounts === undefined ||
                    simulated.value.accounts[0] === null ||
                    simulated.value.accounts[1] === null
                )
                    return logMessage('Unable to simulate transaction.');

                const ammAccountInfo = simulated.value.accounts[0];
                const lpMintAccountInfo = simulated.value.accounts[1];

                const ammInfoBuffer = Buffer.from(ammAccountInfo.data[0], 'base64');
                const lpMintInfoBuffer = Buffer.from(lpMintAccountInfo.data[0], 'base64');
                poolKeys = await formatAmmKeysByAccountData(connection, ammId, ammInfoBuffer, lpMintInfoBuffer, new PublicKey(ammAccountInfo.owner));

                // mintIn = FROM WALLET TO RAYDIUM
                // mintOut = FROM RAYDIUM TO WALLET
                let mintIn, mintOut;
                if (poolKeys.baseMint.toBase58() === WSOL.mint) {
                    mintIn = poolKeys.baseMint;
                    mintOut = poolKeys.quoteMint;
                } else if (poolKeys.quoteMint.toBase58() === WSOL.mint) {
                    mintIn = poolKeys.quoteMint;
                    mintOut = poolKeys.baseMint;
                } else {
                    logMessage('Only SOL is supported as purchase asset');
                    return;
                }

                if (buyers.length < 1) return logMessage('No buyers found in CSV', 'error');
                if (buyers.length !== bundleBuyValues.length) return logMessage('Buyers and SOL numbers do not match', 'error');
                if (buyers.length > swapsPerTransaction * transactionsNumber)
                    return logMessage(`Buyers limit excided. Max ${swapsPerTransaction * transactionsNumber} buyers allowed in one bundle.`, 'error');

                let jitoFeePayer: Keypair;
                if (tipPayer === 0) {
                    jitoFeePayer = wallet;
                } else if (tipPayer === 1) {
                    jitoFeePayer = buyers[0];
                } else {
                    jitoFeePayer = buyers[buyers.length - 1];
                }

                const swapInstructionsArray: InnerSimpleV0Transaction[][] = [[], [], [], []];
                const signersArray: Keypair[][] = [[], [], [], []];
                for (let i = 0; i < buyers.length; i++) {
                    const swapInstructions = await getSwapInstructions(
                        connection,
                        buyers[i],
                        buyers[i],
                        poolKeys,
                        new BN(bundleBuyValues[i] * LAMPORTS_PER_SOL),
                        1,
                        mintIn,
                        mintOut,
                        'in',
                        1000,
                    );

                    swapInstructionsArray[i % transactionsNumber].push(...swapInstructions);
                    signersArray[i % transactionsNumber].push(buyers[i]);
                }
                const { blockhash } = await connection.getLatestBlockhash('finalized');
                for (const [transactionNumber, transactionSwapInstructions] of swapInstructionsArray.entries()) {
                    let jitoTipPayer: Keypair | undefined = undefined;
                    let jitoTipAmount: number | undefined = undefined;
                    if (transactionNumber === 0 && tipPayer === 0) {
                        [jitoTipPayer, jitoTipAmount] = [wallet, advancedSettingsJitoTip];
                    } else if (transactionNumber === 0 && tipPayer === 1) {
                        [jitoTipPayer, jitoTipAmount] = [buyers[0], advancedSettingsJitoTip];
                    } else if (transactionNumber === swapInstructionsArray.length - 1 && tipPayer === 2) {
                        [jitoTipPayer, jitoTipAmount] = [buyers[buyers.length - 1], advancedSettingsJitoTip];
                    }
                    const versionedTransaction = (await buildSimpleTransactionWithInstructionsArray({
                        connection,
                        makeTxVersion: TxVersion.V0,
                        payer: signersArray[transactionNumber][0].publicKey,
                        innerTransactions: transactionSwapInstructions,
                        addLookupTableInfo: LOOKUP_TABLE_CACHE,
                        recentBlockhash: blockhash,
                        signers: signersArray[transactionNumber],
                        jitoTipPayer,
                        jitoTipAmount,
                    })) as VersionedTransaction;
                    versionedTransaction.sign([signersArray[transactionNumber][0]]);
                    jitoVersionedTransactions.push(versionedTransaction);
                }

                await spamAndConfirmBundle(connection, jitoVersionedTransactions, jitoFeePayer, advancedSettingsJitoTip, true);
                logMessage(`JITO bundle sent and confirmed`);
            } else if (bundleMode === 'New Mode') {
                //making swap transaction
                const ammId = deployPoolInstructions.address.ammId;
                const lpMint = deployPoolInstructions.address.lpMint;

                const simulated = await connection.simulateTransaction(deploymentVersionedTransaction, {
                    accounts: {
                        encoding: 'base64',
                        addresses: [ammId.toBase58(), lpMint.toBase58()],
                    },
                });

                console.log('deploymentTxSimulationRes', simulated);

                if (simulated.value.err) {
                    logMessage('Deployment tx simulation error', 'error');
                    console.error('Deployment tx simulation error', simulated.value.err);
                    return;
                }

                if (
                    simulated.value.accounts === null ||
                    simulated.value.accounts === undefined ||
                    simulated.value.accounts[0] === null ||
                    simulated.value.accounts[1] === null
                )
                    return logMessage('Unable to simulate transaction.');

                const ammAccountInfo = simulated.value.accounts[0];
                const lpMintAccountInfo = simulated.value.accounts[1];

                const ammInfoBuffer = Buffer.from(ammAccountInfo.data[0], 'base64');
                const lpMintInfoBuffer = Buffer.from(lpMintAccountInfo.data[0], 'base64');
                poolKeys = await formatAmmKeysByAccountData(connection, ammId, ammInfoBuffer, lpMintInfoBuffer, new PublicKey(ammAccountInfo.owner));

                // mintIn = FROM WALLET TO RAYDIUM
                // mintOut = FROM RAYDIUM TO WALLET
                let mintIn: PublicKey, mintOut: PublicKey;
                if (poolKeys.baseMint.toBase58() === WSOL.mint) {
                    mintIn = poolKeys.baseMint;
                    mintOut = poolKeys.quoteMint;
                } else if (poolKeys.quoteMint.toBase58() === WSOL.mint) {
                    mintIn = poolKeys.quoteMint;
                    mintOut = poolKeys.baseMint;
                } else {
                    logMessage('Only SOL is supported as purchase asset');
                    return;
                }

                if (buyers.length < 1) return logMessage('No buyers found in CSV', 'error');
                if (buyers.length !== bundleBuyValues.length) return logMessage('Buyers and SOL numbers do not match', 'error');
                if (buyers.length > swapsPerTransaction * transactionsNumber)
                    return logMessage(`Buyers limit excided. Max ${swapsPerTransaction * transactionsNumber} buyers allowed in one bundle.`, 'error');

                logMessage(`Creating token accounts with Jito..`);
                await createTokenAccountsWithJito(connection, buyers, mintIn, 'processed', advancedSettingsJitoTip, logMessage);
                await createTokenAccountsWithJito(connection, buyers, mintOut, 'processed', advancedSettingsJitoTip, logMessage);

                const tokenAccountsIn = await Promise.all(buyers.map((buyer) => getAssociatedTokenAddress(mintIn, buyer.publicKey)));
                const tokenAccountBalances = await Promise.all(
                    tokenAccountsIn.map((tokenAccount) => connection.getTokenAccountBalance(tokenAccount, 'processed').then((res) => res.value.amount)),
                );
                await transferAndWrapBatchWithJito(
                    connection,
                    buyers
                        .map((buyer, index) => ({
                            account: tokenAccountsIn[index],
                            owner: buyer,
                            amount: BigInt(bundleBuyValues[index] * LAMPORTS_PER_SOL) - BigInt(tokenAccountBalances[index]),
                        }))
                        .filter(({ amount }) => amount > BigInt(0)),
                    advancedSettingsJitoTip,
                );
                const tokenAccountsOut = await Promise.all(buyers.map((buyer) => getAssociatedTokenAddress(mintOut, buyer.publicKey)));
                logMessage(`Token accounts created..`);

                let addresses = [];

                logMessage('Creating swap instructions');

                const swapInstructionsArray: TransactionInstruction[][] = [[], [], [], []];
                const signersArray: Keypair[][] = [[], [], [], []];
                for (let i = 0; i < buyers.length; i++) {
                    const swapInstructions = Liquidity.makeSwapInstruction({
                        poolKeys: poolKeys,
                        userKeys: {
                            tokenAccountIn: tokenAccountsIn[i],
                            tokenAccountOut: tokenAccountsOut[i],
                            owner: buyers[i].publicKey,
                        },
                        amountIn: new BN(bundleBuyValues[i] * LAMPORTS_PER_SOL),
                        amountOut: 1,
                        fixedSide: 'in',
                    }).innerTransaction.instructions;

                    addresses.push(...swapInstructions.map((ix) => ix.keys.map((key) => key.pubkey)).flat());

                    swapInstructionsArray[i % transactionsNumber].push(...swapInstructions);
                    signersArray[i % transactionsNumber].push(buyers[i]);
                }

                logMessage('Creating ALT');

                addresses = addresses.sort((a, b) => (a.toBase58() > b.toBase58() ? 1 : -1)).filter((v, i, a) => i == 0 || a[i - 1].toBase58() != v.toBase58());

                const [createAltIx, alt] = AddressLookupTableProgram.createLookupTable({
                    authority: wallet.publicKey,
                    payer: wallet.publicKey,
                    recentSlot: await connection.getSlot().then((res) => res - 1),
                });

                console.debug(`ALT: ${alt.toBase58()}`);

                let latestBlockhash = await connection.getLatestBlockhash('finalized');

                const createAltTxWithTip = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: wallet.publicKey,
                        recentBlockhash: latestBlockhash.blockhash,
                        instructions: [await getTipInstructions(wallet, advancedSettingsJitoTip), createAltIx],
                    }).compileToV0Message(),
                );
                createAltTxWithTip.sign([wallet]);

                const createAltTxWithTipSimulateRes = await connection.simulateTransaction(createAltTxWithTip);

                console.debug('createAltTxWithTipSimulateRes', createAltTxWithTipSimulateRes);

                if (createAltTxWithTipSimulateRes.value.err) throw createAltTxWithTipSimulateRes.value.err;

                const altTxs = [createAltTxWithTip];

                const amountOfAddressesPerTx = 30;

                for (let i = 0; i < addresses.length; i += amountOfAddressesPerTx) {
                    const extendAltTx = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: wallet.publicKey,
                            recentBlockhash: latestBlockhash.blockhash,
                            instructions: [
                                AddressLookupTableProgram.extendLookupTable({
                                    authority: wallet.publicKey,
                                    payer: wallet.publicKey,
                                    lookupTable: alt,
                                    addresses: addresses.slice(i, Math.min(i + amountOfAddressesPerTx, addresses.length)),
                                }),
                            ],
                        }).compileToV0Message(),
                    );

                    extendAltTx.sign([wallet]);

                    altTxs.push(extendAltTx);
                }

                const altBundleId = await sendBundle(connection, altTxs, wallet, advancedSettingsJitoTip, true);

                console.debug('altBundleId', altBundleId);

                const createAltTxWithTipSig = bs58.encode(createAltTxWithTip.signatures[0]);

                console.debug('createAltTxWithTipSig', createAltTxWithTipSig);

                logMessage(`Sent create and extend ALT bundle. Bundle id: ${altBundleId}. Create ALT tx signature: ${createAltTxWithTipSig}`);

                latestBlockhash = await connection.getLatestBlockhash();

                await connection.confirmTransaction({
                    ...latestBlockhash,
                    signature: createAltTxWithTipSig,
                });

                logMessage('Created and extended ALT bundle', 'success');

                const alta = new AddressLookupTableAccount({
                    key: alt,
                    state: AddressLookupTableAccount.deserialize(await connection.getAccountInfo(alt).then((res) => res!.data)),
                });

                logMessage('Creating tx for bundle');

                latestBlockhash = await connection.getLatestBlockhash('finalized');

                for (const [transactionNumber, swapInstructions] of swapInstructionsArray.entries()) {
                    const versionedTransaction = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: signersArray[transactionNumber][0].publicKey,
                            recentBlockhash: latestBlockhash.blockhash,
                            instructions: swapInstructions,
                        }).compileToV0Message([alta]),
                    );
                    versionedTransaction.sign(signersArray[transactionNumber]);
                    jitoVersionedTransactions.push(versionedTransaction);
                }

                let jitoFeePayer = tipPayer == 0 ? wallet : tipPayer == 1 ? buyers[0] : buyers[buyers.length - 1];
                const addressLookupTableAccounts = await Promise.all(
                    deploymentVersionedTransaction.message.addressTableLookups.map(async (lookup) => {
                        return new AddressLookupTableAccount({
                            key: lookup.accountKey,
                            state: AddressLookupTableAccount.deserialize(await connection.getAccountInfo(lookup.accountKey).then((res) => res!.data)),
                        });
                    }),
                );
                const message = TransactionMessage.decompile(deploymentVersionedTransaction.message, {
                    addressLookupTableAccounts: addressLookupTableAccounts,
                });
                message.instructions.push(await getTipInstructions(jitoFeePayer, advancedSettingsJitoTip));
                deploymentVersionedTransaction.message = message.compileToV0Message(addressLookupTableAccounts);
                deploymentVersionedTransaction.sign(wallet.publicKey.toBase58() == jitoFeePayer.publicKey.toBase58() ? [wallet] : [wallet, jitoFeePayer]);
                jitoVersionedTransactions.unshift(deploymentVersionedTransaction);

                await spamAndConfirmBundle(connection, jitoVersionedTransactions, jitoFeePayer, advancedSettingsJitoTip, true);

                sigToConfirm = bs58.encode(deploymentVersionedTransaction.signatures[0]);

                logMessage(`Deploy bundle sent and confirmed`);
            }
        } else if (advancedSettingsStatus && advancedSettingsIsJitoTx) {
            const id = await spamAndConfirmBundle(connection, jitoVersionedTransactions, wallet, advancedSettingsJitoTip);

            logMessage(`JITO bundle sent: ${id}`);
        } else {
            const signature = await connection.sendTransaction(deploymentVersionedTransaction, { skipPreflight: advancedSettingsSkipPreflight });

            logMessage(`Transaction sent: ${signature}`);
        }

        injectPoolKeys(poolKeys!);
    }

    return (
        <section>
            <h2>1. Import OpenBook Market ID</h2>
            <section className='openbook-market-id-section'>
                <p>OpenBook Market ID:</p>
                <input type='text' value={openBookMarketId} onChange={openBookMarketIdChange} />
                <button onClick={() => fetchOpenbookMarketInfo(connection, wallet, openBookMarketId)}>Confirm</button>
            </section>
            <h2>2. Price & Initial Liquidity</h2>
            <section>
                <section className='deploy-pool-details'>
                    <div className='deploy-pool-row'>
                        <p>OpenBook Market ID:</p>
                        <div className='deploy-pool-row--right'>
                            {quoteTokenMint && baseTokenMint && <p>{openBookMarketId}</p>}
                            <button className={isMarketIdCopied ? 'copied-button' : 'copy-button'} onClick={() => copyMarketId(openBookMarketId)}></button>
                        </div>
                    </div>
                    <div className='deploy-pool-row'>
                        <p>Base Token Mint Address:</p>
                        <div className='deploy-pool-row--right'>
                            <p>{baseTokenMint}</p>
                            <button className={isBaseTokenCopied ? 'copied-button' : 'copy-button'} onClick={() => copyBaseToken(baseTokenMint)}></button>
                        </div>
                    </div>
                    <div className='deploy-pool-row'>
                        <p>Quote Token Mint Address:</p>
                        <div className='deploy-pool-row--right'>
                            <p>{quoteTokenMint}</p>
                            <button className={isQuoteTokenCopied ? 'copied-button' : 'copy-button'} onClick={() => copyQuoteToken(quoteTokenMint)}></button>
                        </div>
                    </div>
                </section>
                <section className='initial-liquidity-section'>
                    <div className='initial-liquidity'>
                        <div className='initial-liquidity-row'>
                            <p>Base Token Initial Liquidity:</p>
                            <input type='number' value={baseTokenInitialLiquidity} onChange={baseTokenInitialLiquidityChange} />
                        </div>
                        <div className='initial-liquidity-row'>
                            <p>
                                Balance: <span>{baseTokenBalance}</span>
                            </p>
                            <button onClick={maxButtonForBaseToken}>MAX</button>
                        </div>
                    </div>
                    <div className='initial-liquidity'>
                        <div className='initial-liquidity-row'>
                            <p>Quote Token Initial Liquidity:</p>
                            <input type='number' value={quoteTokenInitialLiquidity} onChange={quoteTokenInitialLiquidityChange} />
                        </div>
                        <div className='initial-liquidity-row'>
                            <p>
                                Balance: <span>{quoteTokenBalance}</span>
                            </p>
                            <button onClick={maxButtonForQuoteToken}>MAX</button>
                        </div>
                    </div>
                </section>
            </section>
            <section className='advanced-settings-container'>
                <div className='advanced-settings-header'>
                    <h2>Advanced Settings</h2>
                    <label className='switch'>
                        <input type='checkbox' checked={advancedSettingsStatus} onChange={advancedSettingsStatusChange} />
                        <span className='slider round'></span>
                    </label>
                </div>
                <div>
                    {advancedSettingsStatus && (
                        <section className='advanced-settings-wrap'>
                            <div className='advanced-settings-option'>
                                <div className='advanced-settings-jito-container'>
                                    <div className='jito-tx'>
                                        <input type='checkbox' checked={advancedSettingsIsJitoTx} onChange={advancedSettingsIsJitoTxChange} />
                                        <p>JITO tx</p>
                                    </div>
                                    <div className='jito-tip'>
                                        <input
                                            type='number'
                                            value={advancedSettingsJitoTip / LAMPORTS_PER_SOL}
                                            onChange={advancedSettingsJitoTipChange}
                                            disabled={!advancedSettingsIsJitoTx}
                                        />
                                        <p>JITO tip SOL</p>
                                    </div>
                                </div>
                                {advancedSettingsIsJitoTx && (
                                    <div className='advanced-settings-add-purchase'>
                                        <input
                                            type='checkbox'
                                            checked={advancedSettingsAddPurchaseStatus}
                                            onChange={advancedSettingsAddPurchaseStatusChange}
                                            disabled={!advancedSettingsIsJitoTx || !bundleBuyValues.length}
                                        />
                                        <p>Add purchase inside of initial transaction (SOL)</p>
                                    </div>
                                )}
                                {advancedSettingsAddPurchaseStatus && advancedSettingsIsJitoTx && (
                                    <>
                                        <div className='mode-option-radio'>
                                            <label>
                                                <input
                                                    type='radio'
                                                    name='mode'
                                                    value='Classic'
                                                    checked={bundleMode === 'Classic'}
                                                    onChange={bundleModeChange}
                                                />
                                                <span>Classic</span>
                                            </label>
                                            <label>
                                                <input
                                                    type='radio'
                                                    name='mode'
                                                    value='New Mode'
                                                    checked={bundleMode === 'New Mode'}
                                                    onChange={bundleModeChange}
                                                />
                                                <span>New Mode</span>
                                            </label>
                                        </div>
                                        <div className='download-csv-container'>
                                            <label>
                                                Jito Tip payer:
                                                <select className='commission-payer-select' value={tipPayer} onChange={feePayerChange}>
                                                    {tipPayerOptions.map((option, index) => (
                                                        <option value={option} key={option}>
                                                            {tipPayerLabels[index]}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className='advanced-settings-option'>
                                <div className='advanced-opt-btn'>
                                    <div className='advanced-settings-delayed-start'>
                                        <div className='delayed-start-header'>
                                            <input type='checkbox' checked={startTimeDelaySecondsStatus} onChange={startTimeDelaySecondsStatusChange} />
                                            <p>Delayed Start</p>
                                        </div>
                                        {startTimeDelaySecondsStatus && (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                                                    label='Start Time (UTC)'
                                                    timezone='UTC'
                                                    ampm={false}
                                                    value={startTimeDelayDate}
                                                    onChange={startTimeDelayDateChange}
                                                    disablePast
                                                />
                                                <p>Epoch: {startTimeDelayDate ? startTimeEpoch : 'N/A'}</p>
                                            </LocalizationProvider>
                                        )}
                                    </div>
                                    <div className='advanced-settings-skip-preflight'>
                                        <input type='checkbox' checked={advancedSettingsSkipPreflight} onChange={advancedSettingsSkipPreflightChange} />
                                        <p>Skip Preflight</p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </section>
            <button className='button-accent button-250' onClick={() => deployAmmPool(connection, wallet)}>
                Deploy Pool
            </button>
        </section>
    );
}
